import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DaySpecificHours } from '@/components/shift-templates/DaySpecificHours';
import { ShiftAlarmSettings } from '@/components/shift-templates/ShiftAlarmSettings';

import {
    useCreateShiftTemplate,
    useUpdateShiftTemplate
} from '@/hooks/useShiftTemplates';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { track } from '@/lib/analytics';
import { DaySpecificTimes } from '@/types/shift-templates';

import { Loader2, Clock, CalendarDays, Moon, Sun, Sunrise, Sunset, Timer, Briefcase, PenLine, ArrowRight, ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { usePatientRole } from '@/hooks/usePatientRole';
import { PATIENT_INACTIVE_TOAST_MESSAGE, isPatientInactiveRlsError } from '@/lib/patient-status-errors';

const SHIFT_PRESETS = [
    { key: 'day12h', name: 'preset.day12h', icon: Sun, start: '07:00', end: '19:00', overnight: false },
    { key: 'night12h', name: 'preset.night12h', icon: Moon, start: '19:00', end: '07:00', overnight: true },
    { key: 'morning6h', name: 'preset.morning6h', icon: Sunrise, start: '07:00', end: '13:00', overnight: false },
    { key: 'afternoon6h', name: 'preset.afternoon6h', icon: Sunset, start: '13:00', end: '19:00', overnight: false },
    { key: 'full24h', name: 'preset.full24h', icon: Timer, start: '07:00', end: '07:00', overnight: true },
    { key: 'business8h', name: 'preset.business8h', icon: Briefcase, start: '08:00', end: '17:00', overnight: false },
] as const;

const SCRATCH_KEY = '_scratch';

const formSchema = z.object({
    name: z.string().min(1, 'Required'),
    default_start_time: z.string().min(1, 'Required'),
    default_end_time: z.string().min(1, 'Required'),
    has_variable_hours: z.boolean().default(false),
    is_overnight: z.boolean().default(false),
    auto_rollover: z.boolean().default(false),
    alarm_enabled: z.boolean().default(false),
    alarm_sound: z.string().default('bell'),
    alarm_vibrate: z.boolean().default(true),
    alarm_repeat_minutes: z.coerce.number().min(1).max(60).default(5),
    alarm_max_repeats: z.coerce.number().min(0).max(20).default(3),
});


type FormData = z.infer<typeof formSchema>;

/* ── Step Indicator ── */
function StepIndicator({ current, total }: { current: number; total: number }) {
    return (
        <div className="flex items-center justify-center gap-2 py-3">
            {Array.from({ length: total }, (_, i) => (
                <div
                    key={i}
                    className={cn(
                        'h-2 rounded-full transition-all duration-300',
                        i + 1 === current ? 'w-8 bg-primary' : 'w-2 bg-muted-foreground/30',
                        i + 1 < current && 'bg-primary/50'
                    )}
                />
            ))}
        </div>
    );
}

/* ── Step 1: Preset Selection ── */
function PresetStep({
    selectedPreset,
    onSelect,
    onNext,
    t,
}: {
    selectedPreset: string | null;
    onSelect: (key: string) => void;
    onNext: () => void;
    t: any;
}) {
    return (
        <div className="p-4 min-h-[60vh] flex flex-col">
            <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground mb-1">
                    {t('shiftTemplates.step1Title')}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {t('shiftTemplates.orChoosePreset')}
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 flex-1">
                {SHIFT_PRESETS.map((preset) => {
                    const Icon = preset.icon;
                    const isSelected = selectedPreset === preset.key;
                    return (
                        <button
                            key={preset.key}
                            type="button"
                            onClick={() => onSelect(preset.key)}
                            className={cn(
                                'relative flex flex-col items-center gap-2 p-4 rounded-xl border bg-card transition-all text-center group',
                                isSelected
                                    ? 'border-primary ring-2 ring-primary/20 shadow-md'
                                    : 'hover:border-primary/50 hover:shadow-sm'
                            )}
                        >
                            {isSelected && (
                                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                    <Check className="w-3 h-3 text-primary-foreground" />
                                </div>
                            )}
                            <div className={cn(
                                'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                                isSelected ? 'bg-primary/20' : 'bg-primary/10 group-hover:bg-primary/15'
                            )}>
                                <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <span className="text-sm font-medium text-foreground">
                                {t(`shiftTemplates.${preset.name}`)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {preset.start} – {preset.end}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Create from scratch */}
            <button
                type="button"
                onClick={() => onSelect(SCRATCH_KEY)}
                className={cn(
                    'w-full flex items-center justify-center gap-2 py-3 rounded-lg border transition-colors text-sm mb-6',
                    selectedPreset === SCRATCH_KEY
                        ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/20'
                        : 'border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary'
                )}
            >
                <PenLine className="w-4 h-4" />
                {t('shiftTemplates.createFromScratch')}
            </button>

            <div className="mt-auto pt-4">
                <Button
                    size="lg"
                    className="w-full h-12 text-lg shadow-md"
                    disabled={!selectedPreset}
                    onClick={onNext}
                >
                    {t('shiftTemplates.next')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
        </div>
    );
}

/* ── Main Page ── */
export default function ShiftTemplateFormPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { patientId, templateId } = useParams<{ patientId: string; templateId?: string }>();
    const isEditing = !!templateId && templateId !== 'new';
    const { isPatientActive, status, canManageSchedules, isLoading: isRoleLoading } = usePatientRole(patientId);

    const blockedReason: 'archived' | 'suspended' | 'permission' | null = !isPatientActive
        ? (status === 'archived' ? 'archived' : 'suspended')
        : (!isRoleLoading && !canManageSchedules ? 'permission' : null);
    const blockedMessage = blockedReason
        ? t(`shiftTemplates.blocked${blockedReason.charAt(0).toUpperCase()}${blockedReason.slice(1)}`)
        : null;

    const [daySpecificTimes, setDaySpecificTimes] = useState<DaySpecificTimes>({});
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(isEditing);
    const [step, setStep] = useState<1 | 2>(isEditing ? 2 : 1);
    const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
    const [activeShiftCount, setActiveShiftCount] = useState(0);
    const [pendingSubmit, setPendingSubmit] = useState<FormData | null>(null);
    const originalHoursRef = useRef<string | null>(null);

    const createMutation = useCreateShiftTemplate();
    const updateMutation = useUpdateShiftTemplate();
    const isSaving = createMutation.isPending || updateMutation.isPending;


    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            default_start_time: '07:00',
            default_end_time: '19:00',
            has_variable_hours: false,
            is_overnight: false,
            auto_rollover: false,
            alarm_enabled: false,
            alarm_sound: 'bell',
            alarm_vibrate: true,
            alarm_repeat_minutes: 5,
            alarm_max_repeats: 3,
        },

    });

    const hasVariableHours = form.watch('has_variable_hours');
    const defaultStartTime = form.watch('default_start_time');
    const defaultEndTime = form.watch('default_end_time');

    useEffect(() => {
        if (isEditing && templateId) {
            fetchTemplate(templateId);
        }
    }, [templateId]);

    // Quantos plantões em andamento usam esta escala (para avisar sobre o ajuste de atividades)
    useEffect(() => {
        if (!isEditing || !templateId || !patientId) return;
        let cancelled = false;

        (async () => {
            const { data } = await supabase
                .from('shifts')
                .select('id, shift_template_id, original_template_id')
                .eq('patient_id', patientId)
                .in('status', ['active', 'in_progress']);

            if (cancelled) return;
            const count = (data || []).filter(
                (s) => s.shift_template_id === templateId || s.original_template_id === templateId,
            ).length;
            setActiveShiftCount(count);
        })();

        return () => { cancelled = true; };
    }, [isEditing, templateId, patientId]);


    const fetchTemplate = async (id: string) => {
        try {
            const { data, error } = await supabase
                .from('shift_templates')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;

            if (data) {
                form.reset({
                    name: data.name,
                    default_start_time: data.default_start_time.substring(0, 5),
                    default_end_time: data.default_end_time.substring(0, 5),
                    has_variable_hours: data.has_variable_hours || false,
                    is_overnight: data.is_overnight || false,
                    auto_rollover: (data as any).auto_rollover ?? false,
                    alarm_enabled: (data as any).alarm_enabled ?? false,
                    alarm_sound: (data as any).alarm_sound ?? 'bell',
                    alarm_vibrate: (data as any).alarm_vibrate ?? true,
                    alarm_repeat_minutes: (data as any).alarm_repeat_minutes ?? 5,
                    alarm_max_repeats: (data as any).alarm_max_repeats ?? 3,
                });

                setDaySpecificTimes((data.day_specific_times as any) || {});
                originalHoursRef.current = JSON.stringify({
                    start: data.default_start_time.substring(0, 5),
                    end: data.default_end_time.substring(0, 5),
                    overnight: !!data.is_overnight,
                    variable: !!data.has_variable_hours,
                    days: data.has_variable_hours ? ((data.day_specific_times as any) || {}) : {},
                });
            }

        } catch (error) {
            console.error('Error fetching template:', error);
            toast.error(t('common.error'));
            navigate(-1);
        } finally {
            setIsLoadingTemplate(false);
        }
    };

    // Auto-detect overnight based on times
    useEffect(() => {
        const startHour = parseInt(defaultStartTime.split(':')[0] || '0');
        const endHour = parseInt(defaultEndTime.split(':')[0] || '0');

        if (endHour < startHour || (endHour === startHour && defaultEndTime < defaultStartTime)) {
            form.setValue('is_overnight', true);
        }
    }, [defaultStartTime, defaultEndTime, form]);

    const handleNextStep = () => {
        if (!selectedPreset) return;

        const alarmDefaults = {
            auto_rollover: false,
            alarm_enabled: false,
            alarm_sound: 'bell',
            alarm_vibrate: true,
            alarm_repeat_minutes: 5,
            alarm_max_repeats: 3,
        };

        if (selectedPreset !== SCRATCH_KEY) {
            const preset = SHIFT_PRESETS.find(p => p.key === selectedPreset);
            if (preset) {
                form.reset({
                    name: t(`shiftTemplates.${preset.name}`),
                    default_start_time: preset.start,
                    default_end_time: preset.end,
                    is_overnight: preset.overnight,
                    has_variable_hours: false,
                    ...alarmDefaults,
                });
            }
        } else {
            form.reset({
                name: '',
                default_start_time: '07:00',
                default_end_time: '19:00',
                has_variable_hours: false,
                is_overnight: false,
                ...alarmDefaults,
            });
        }

        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
    };

    const hoursChanged = (data: FormData) => {
        if (!originalHoursRef.current) return false;
        return originalHoursRef.current !== JSON.stringify({
            start: data.default_start_time,
            end: data.default_end_time,
            overnight: !!data.is_overnight,
            variable: !!data.has_variable_hours,
            days: data.has_variable_hours ? daySpecificTimes : {},
        });
    };

    const onSubmit = async (data: FormData) => {
        if (isEditing && activeShiftCount > 0 && hoursChanged(data)) {
            setPendingSubmit(data);
            return;
        }
        await performSubmit(data);
    };

    const performSubmit = async (data: FormData) => {

        if (!patientId) return;
        if (!isPatientActive) {
            toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
            return;
        }
        try {
            if (isEditing && templateId) {
                await updateMutation.mutateAsync({
                    id: templateId,
                    patientId,
                    name: data.name,
                    default_start_time: data.default_start_time,
                    default_end_time: data.default_end_time,
                    has_variable_hours: data.has_variable_hours,
                    is_overnight: data.is_overnight,
                    day_specific_times: data.has_variable_hours ? daySpecificTimes : null,
                    auto_rollover: data.auto_rollover,
                    alarm_enabled: data.alarm_enabled,
                    alarm_sound: data.alarm_sound,
                    alarm_vibrate: data.alarm_vibrate,
                    alarm_repeat_minutes: data.alarm_repeat_minutes,
                    alarm_max_repeats: data.alarm_max_repeats,
                });


                captureEvent('shift_template_updated', {
                    patient_id: patientId,
                    template_id: templateId,
                    name: data.name,
                    is_overnight: data.is_overnight,
                    has_variable_hours: data.has_variable_hours
                });
            } else {
                const created = await createMutation.mutateAsync({
                    patient_id: patientId,
                    name: data.name,
                    default_start_time: data.default_start_time,
                    default_end_time: data.default_end_time,
                    has_variable_hours: data.has_variable_hours,
                    is_overnight: data.is_overnight,
                    day_specific_times: data.has_variable_hours ? daySpecificTimes : null,
                    auto_rollover: data.auto_rollover,
                    alarm_enabled: data.alarm_enabled,
                    alarm_sound: data.alarm_sound,
                    alarm_vibrate: data.alarm_vibrate,
                    alarm_repeat_minutes: data.alarm_repeat_minutes,
                    alarm_max_repeats: data.alarm_max_repeats,
                });


                // Compute duration in hours (handles overnight wrap).
                const [sh, sm] = data.default_start_time.split(':').map(Number);
                const [eh, em] = data.default_end_time.split(':').map(Number);
                let durationHours = (eh + em / 60) - (sh + sm / 60);
                if (data.is_overnight || durationHours <= 0) durationHours += 24;

                const presetKey = selectedPreset && selectedPreset !== SCRATCH_KEY ? selectedPreset : 'scratch';

                const canonicalProps = {
                    patient_id: patientId,
                    template_id: (created as { id?: string } | undefined)?.id ?? null,
                    name: data.name,
                    is_overnight: data.is_overnight,
                    has_variable_hours: data.has_variable_hours,
                    duration_hours: Math.round(durationHours * 100) / 100,
                    preset: presetKey,
                    day_count: data.has_variable_hours ? Object.keys(daySpecificTimes ?? {}).length : 7,
                };

                track('shift_template_created', canonicalProps);

            }
            navigate(-1);
        } catch (error) {
            if (isPatientInactiveRlsError(error)) {
                toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
            }
            // Error handled by mutation
        }
    };

    if (isLoadingTemplate) {
        return (
            <AppLayout title={t('common.loading')} showBack onBack={() => navigate(-1)}>
                <div className="flex items-center justify-center p-8">
                    <CareOSLoader size="lg" />
                </div>
            </AppLayout>
        );
    }

    const title = isEditing
        ? t('shiftTemplates.editTitle')
        : step === 1
            ? t('shiftTemplates.step1Title')
            : t('shiftTemplates.step2Title');

    const handleAppBack = () => {
        if (!isEditing && step === 2) {
            handleBack();
        } else {
            navigate(-1);
        }
    };

    return (
        <AppLayout
            title={title}
            showBack
            onBack={handleAppBack}
            showFAB={false}
        >
            <div className="bg-muted/5 min-h-screen">
                {blockedMessage && (
                    <div className="m-4 p-3 rounded-lg border border-warning bg-warning/10 text-sm text-warning-foreground">
                        {blockedMessage}
                    </div>
                )}
                {/* Step indicator (only for creation) */}
                {!isEditing && (
                    <StepIndicator current={step} total={2} />
                )}

                {/* Step 1: Preset selection */}
                {step === 1 && !isEditing && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                        <PresetStep
                            selectedPreset={selectedPreset}
                            onSelect={setSelectedPreset}
                            onNext={handleNextStep}
                            t={t}
                        />
                    </div>
                )}

                {/* Step 2: Form */}
                {step === 2 && (
                    <div className="p-4 animate-in fade-in slide-in-from-right-4 duration-300">
                        {/* Back button for creation mode */}
                        {!isEditing && (
                            <button
                                type="button"
                                onClick={handleBack}
                                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                {t('shiftTemplates.changeModel')}
                            </button>
                        )}

                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                {/* Basic Info */}
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm flex items-center gap-2">
                                            <CalendarDays className="w-4 h-4 text-primary" />
                                            {t('shiftTemplates.basicInfo')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <FormField
                                            control={form.control}
                                            name="name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('shiftTemplates.templateName')}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder={t('shiftTemplates.templateNamePlaceholder')} {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </CardContent>
                                </Card>

                                {/* Schedule Configuration */}
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-primary" />
                                            {t('shiftTemplates.scheduleConfig')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <FormField
                                                control={form.control}
                                                name="default_start_time"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t('shiftTemplates.startTime')}</FormLabel>
                                                        <FormControl>
                                                            <Input type="time" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="default_end_time"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t('shiftTemplates.endTime')}</FormLabel>
                                                        <FormControl>
                                                            <Input type="time" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        <FormField
                                            control={form.control}
                                            name="is_overnight"
                                            render={({ field }) => (
                                                <FormItem className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
                                                    <div className="flex items-center gap-3">
                                                        <Moon className="w-5 h-5 text-indigo-500" />
                                                        <div>
                                                            <FormLabel className="text-base font-medium cursor-pointer">
                                                                {t('shiftTemplates.overnightShift')}
                                                            </FormLabel>
                                                            <FormDescription className="text-xs">
                                                                {t('shiftTemplates.overnightDescription', "Shift spans across midnight")}
                                                            </FormDescription>
                                                        </div>
                                                    </div>
                                                    <FormControl>
                                                        <Switch
                                                            checked={field.value}
                                                            onCheckedChange={field.onChange}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="has_variable_hours"
                                            render={({ field }) => (
                                                <FormItem className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
                                                    <div className="flex items-center gap-3">
                                                        <CalendarDays className="w-5 h-5 text-success" />
                                                        <div>
                                                            <FormLabel className="text-base font-medium cursor-pointer">
                                                                {t('shiftTemplates.variableHours')}
                                                            </FormLabel>
                                                            <FormDescription className="text-xs">
                                                                {t('shiftTemplates.daySpecificHours')}
                                                            </FormDescription>
                                                        </div>
                                                    </div>
                                                    <FormControl>
                                                        <Checkbox
                                                            checked={field.value}
                                                            onCheckedChange={field.onChange}
                                                            className="h-5 w-5"
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />

                                        {hasVariableHours && (
                                            <div className="border-t pt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                <DaySpecificHours
                                                    value={daySpecificTimes}
                                                    onChange={setDaySpecificTimes}
                                                    defaultStart={defaultStartTime}
                                                    defaultEnd={defaultEndTime}
                                                />
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Alarm Configuration */}
                                <ShiftAlarmSettings
                                    control={form.control}
                                    alarmEnabled={form.watch('alarm_enabled')}
                                    currentSound={(form.watch('alarm_sound') as any) || 'bell'}
                                    vibrate={form.watch('alarm_vibrate')}
                                />

                                {/* Plantão contínuo (virada automática) */}
                                <Card>
                                    <CardContent className="p-4">
                                        <FormField
                                            control={form.control}
                                            name="auto_rollover"
                                            render={({ field }) => (
                                                <FormItem className="flex items-center justify-between gap-4">
                                                    <div className="space-y-1">
                                                        <FormLabel className="text-base">
                                                            {t('shiftTemplates.autoRollover')}
                                                        </FormLabel>
                                                        <FormDescription>
                                                            {t('shiftTemplates.autoRolloverDesc')}
                                                        </FormDescription>
                                                    </div>
                                                    <FormControl>
                                                        <Switch
                                                            checked={field.value}
                                                            onCheckedChange={field.onChange}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                    </CardContent>
                                </Card>



                                {isEditing && activeShiftCount > 0 && (
                                    <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 flex gap-2">
                                        <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                                        <div className="space-y-1 text-xs text-muted-foreground">
                                            <p className="font-medium text-foreground">{t('shiftTemplates.activeShiftSyncTitle')}</p>
                                            <p>{t('shiftTemplates.activeShiftSyncDescription')}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-4 space-y-2">
                                    <Button type="submit" size="lg" className="w-full h-12 text-lg shadow-md" disabled={isSaving || !!blockedReason}>
                                        {isSaving && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                                        {isSaving ? t('shiftTemplates.saving') : t('shiftTemplates.save')}
                                    </Button>
                                    {blockedMessage && (
                                        <p className="text-xs text-warning-foreground text-center">{blockedMessage}</p>
                                    )}
                                </div>
                            </form>
                        </Form>
                    </div>
                )}
            </div>

            <AlertDialog open={!!pendingSubmit} onOpenChange={(open) => { if (!open) setPendingSubmit(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('shiftTemplates.activeShiftSyncTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('shiftTemplates.activeShiftSyncDescription')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                        <li>{t('shiftTemplates.activeShiftSyncAdd')}</li>
                        <li>{t('shiftTemplates.activeShiftSyncRemove')}</li>
                        <li>{t('shiftTemplates.activeShiftSyncKeep')}</li>
                        <li>{t('shiftTemplates.activeShiftSyncNotify')}</li>
                    </ul>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isSaving}>{t('shiftTemplates.activeShiftSyncCancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isSaving}
                            onClick={(e) => {
                                e.preventDefault();
                                const data = pendingSubmit;
                                setPendingSubmit(null);
                                if (data) void performSubmit(data);
                            }}
                        >
                            {t('shiftTemplates.activeShiftSyncConfirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppLayout>

    );
}
